<template>
  <v-tooltip :text="label" location="right" :disabled="expanded || tooltipDisabled">
    <template #activator="{ props: tooltipProps }">
      <component
        :is="route ? 'router-link' : 'button'"
        v-bind="{ ...tooltipProps, ...$attrs }"
        :to="route ? { name: route } : undefined"
        class="sidebar-nav-item"
        :class="{
          'sidebar-nav-item--active': isActive,
          'sidebar-nav-item--expanded': expanded,
        }"
        @click="$emit('click', $event)">
        <span class="sidebar-nav-item__icon-wrap">
          <v-icon :icon="icon" size="20" class="sidebar-nav-item__icon" />
          <v-icon
            v-if="submenu && !expanded"
            icon="mdi-menu-right"
            size="14"
            class="sidebar-nav-item__submenu-arrow--rail" />
          <transition name="badge-pop">
            <span
              v-if="count > 0 && !expanded"
              class="sidebar-nav-item__badge"
              :class="{ 'sidebar-nav-item__badge--pulse': pulsing }">
              {{ displayCount }}
            </span>
          </transition>
        </span>
        <span v-show="expanded" class="sidebar-nav-item__label">{{ label }}</span>
        <v-icon v-if="submenu && expanded" icon="mdi-menu-right" size="18" class="sidebar-nav-item__submenu-arrow" />
        <transition name="badge-pop">
          <span
            v-if="count > 0 && expanded"
            class="sidebar-nav-item__pill"
            :class="{ 'sidebar-nav-item__pill--pulse': pulsing }">
            {{ displayCount }}
          </span>
        </transition>
      </component>
    </template>
  </v-tooltip>
</template>

<script>
export default { inheritAttrs: false };
</script>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  icon: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  route: {
    type: String,
    default: "",
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  count: {
    type: Number,
    default: 0,
  },
  submenu: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  tooltipDisabled: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["click"]);

const currentRoute = useRoute();

const isActive = computed(() => {
  if (props.active) return true;
  if (!props.route) return false;
  return currentRoute.name === props.route;
});

const displayCount = computed(() => (props.count > 99 ? "99+" : String(props.count)));

const pulsing = ref(false);
let pulseTimer = null;

watch(
  () => props.count,
  (next, prev) => {
    if (next > prev && next > 0) {
      pulsing.value = false;
      clearTimeout(pulseTimer);
      requestAnimationFrame(() => {
        pulsing.value = true;
        pulseTimer = setTimeout(() => (pulsing.value = false), 700);
      });
    }
  }
);
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.sidebar-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 0;
  border: none;
  background: transparent;
  color: $lh_sidebar_text;
  text-decoration: none;
  cursor: pointer;
  position: relative;
  border-radius: 8px;
  transition: color $lh_transition_fast, background-color $lh_transition_fast;
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  font-weight: 500;
  justify-content: center;

  &:hover {
    color: $lh_sidebar_text_active;
    background: $lh_sidebar_bg_hover;
  }
}

.sidebar-nav-item--expanded {
  padding: 10px 16px;
  justify-content: flex-start;
}

.sidebar-nav-item--active {
  color: $lh_sidebar_text_active;
  background: $lh_sidebar_bg_hover;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 6px;
    bottom: 6px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: $lh_sidebar_accent;
  }
}

.sidebar-nav-item__icon-wrap {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.sidebar-nav-item__badge {
  position: absolute;
  top: -8px;
  left: 55%;
  box-sizing: border-box;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 9px;
  background: $lh_sidebar_accent;
  border: 2px solid $lh_sidebar_bg;
  color: #ffffff;
  font-size: 9.5px;
  font-weight: 700;
  line-height: 13px;
  text-align: center;
}

.sidebar-nav-item__pill {
  margin-left: auto;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: $lh_sidebar_accent;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.sidebar-nav-item__badge--pulse,
.sidebar-nav-item__pill--pulse {
  animation: badgePulse 600ms cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes badgePulse {
  0% {
    transform: scale(0.85);
  }
  35% {
    transform: scale(1.18);
  }
  100% {
    transform: scale(1);
  }
}

.badge-pop-enter-active {
  transition: transform $lh_transition_fast, opacity $lh_transition_fast;
}

.badge-pop-enter-from {
  transform: scale(0.6);
  opacity: 0;
}

.badge-pop-leave-active {
  transition: transform $lh_transition_fast, opacity $lh_transition_fast;
}

.badge-pop-leave-to {
  transform: scale(0.6);
  opacity: 0;
}

.sidebar-nav-item__label {
  opacity: 0;
  transition: opacity $lh_transition_fast;
  white-space: nowrap;

  .sidebar-nav-item--expanded & {
    opacity: 1;
  }
}

.sidebar-nav-item__submenu-arrow {
  margin-left: auto;
  opacity: 0.7;
}

.sidebar-nav-item__submenu-arrow--rail {
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.7;
}
</style>
